import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Skeleton,
  Alert, IconButton, Select, MenuItem,
} from "@mui/material";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import ShieldRoundedIcon         from "@mui/icons-material/ShieldRounded";
import ReceiptLongRoundedIcon    from "@mui/icons-material/ReceiptLongRounded";
import TrendingUpRoundedIcon     from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon   from "@mui/icons-material/TrendingDownRounded";
import CurrencyRupeeRoundedIcon  from "@mui/icons-material/CurrencyRupeeRounded";
import KeyboardArrowLeft         from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight        from "@mui/icons-material/KeyboardArrowRight";
import InventoryRoundedIcon      from "@mui/icons-material/InventoryRounded";
import ProtectionNav from "./ProtectionNav";
import { getData } from "../../services/apiService";
import { API_ENDPOINTS } from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  emerald: "#059669", emeraldLt: "#D1FAE5",
  rose:    "#E11D48", roseLt:   "#FFE4E6",
  amber:   "#D97706", amberLt:  "#FEF3C7",
  indigo:  "#4F46E5", indigoLt: "#EEF2FF",
  orange:  "#FF7900", orangeLt: "#FFF0E0",
  bg:      "#F7FAFC", border: "#E2E8F0",
  text:    "#0F172A", muted:  "#64748B",
};

const fmtRs   = n => `₹${Number(n||0).toLocaleString("en-IN")}`;
const fmtDate = d => d ? new Date(d).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "—";
const fmtShort= d => d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "—";

// ── KPI tile ──────────────────────────────────────────────────────────────
const KpiTile = ({ label, value, icon, accent, iconBg, loading, sub }) => (
  <Card sx={{ flex:"1 1 160px", borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", overflow:"hidden" }}>
    <Box sx={{ height:3, background:`linear-gradient(90deg,${accent},${accent}66)` }} />
    <CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <Box>
          <Typography sx={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.4px", mb:0.8 }}>{label}</Typography>
          {loading ? <Skeleton width={90} height={30} /> :
            <Typography sx={{ fontSize:24, fontWeight:800, color:accent, lineHeight:1.1, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{value}</Typography>
          }
          {sub && <Typography sx={{ fontSize:11, color:accent, opacity:0.8, mt:0.4 }}>{sub}</Typography>}
        </Box>
        <Box sx={{ width:42, height:42, borderRadius:"12px", bgcolor:iconBg, display:"flex", alignItems:"center", justifyContent:"center" }}>
          {React.cloneElement(icon, { sx:{ fontSize:20, color:accent } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ── Sub-tab pill ──────────────────────────────────────────────────────────
const SubTab = ({ label, icon, active, onClick }) => (
  <Box onClick={onClick} sx={{
    display:"flex", alignItems:"center", gap:0.8,
    px:2, py:0.9, borderRadius:"9px", cursor:"pointer",
    bgcolor:active?T.teal:"transparent", color:active?"#fff":T.muted,
    fontWeight:active?700:500, fontSize:13, transition:"all 0.14s",
    "&:hover":{ bgcolor:active?T.teal:T.tealLt, color:active?"#fff":T.teal },
    fontFamily:"'Plus Jakarta Sans',sans-serif",
  }}>
    {React.cloneElement(icon, { sx:{ fontSize:15 } })}
    {label}
  </Box>
);

// ── Plan chip ─────────────────────────────────────────────────────────────
const PlanChip = ({ tier }) => {
  const k = (tier||"").toUpperCase();
  const cfg = { BASIC:{bg:T.tealLt,c:T.teal}, STANDARD:{bg:T.indigoLt,c:T.indigo}, PREMIUM:{bg:T.amberLt,c:T.amber} };
  const { bg, c } = cfg[k]||{bg:"#F1F5F9",c:T.muted};
  return <Box sx={{ display:"inline-flex",px:1.2,py:0.3,bgcolor:bg,borderRadius:"7px",border:`1px solid ${c}22` }}><Typography sx={{ fontSize:11,fontWeight:700,color:c }}>{k||"—"}</Typography></Box>;
};

// ── Skeleton rows ─────────────────────────────────────────────────────────
const SkelRow = ({ cols }) => (
  <TableRow>{Array.from({length:cols}).map((_,i)=><TableCell key={i}><Skeleton width={80} height={16} /></TableCell>)}</TableRow>
);

// ── Pagination bar ────────────────────────────────────────────────────────
const Paginator = ({ page, totalPages, perPage, setPerPage, setPage, total }) => (
  <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", px:2.5, py:1.5, borderTop:`1px solid ${T.border}` }}>
    <Typography sx={{ fontSize:13, color:T.muted }}>Page {page} of {totalPages} · {total} total</Typography>
    <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
      <Select value={perPage} onChange={e=>{setPerPage(Number(e.target.value));setPage(1);}} size="small" sx={{ borderRadius:"10px",fontSize:13 }}>
        {[15,25,50].map(n=><MenuItem key={n} value={n}>{n} per page</MenuItem>)}
      </Select>
      <IconButton size="small" onClick={()=>setPage(p=>Math.max(p-1,1))} disabled={page<=1} sx={{ bgcolor:T.bg,border:`1px solid ${T.border}`,borderRadius:"8px" }}><KeyboardArrowLeft sx={{ fontSize:18 }} /></IconButton>
      <Typography sx={{ fontSize:13,fontWeight:600,color:T.text,minWidth:56,textAlign:"center" }}>{page}/{totalPages}</Typography>
      <IconButton size="small" onClick={()=>setPage(p=>Math.min(p+1,totalPages))} disabled={page>=totalPages} sx={{ bgcolor:T.bg,border:`1px solid ${T.border}`,borderRadius:"8px" }}><KeyboardArrowRight sx={{ fontSize:18 }} /></IconButton>
    </Box>
  </Box>
);

// ── Protections sub-tab ───────────────────────────────────────────────────
function ProtectionsTab() {
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [page,     setPage]     = useState(1);
  const [perPage,  setPerPage]  = useState(15);
  const [total,    setTotal]    = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getData(API_ENDPOINTS.adminGetAllProtections, { page, limit: perPage });
      const r = res?.result || res?.data?.result;
      if (r) { setBookings(r.bookings||[]); setTotal(r.pagination?.total||0); setTotalPages(r.pagination?.totalPages||1); }
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [page, perPage]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card sx={{ borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", overflow:"hidden" }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor:T.bg }}>
              {["Certificate","Tracking Code","Customer","Plan","Declared Value","Premium","Trip","Status","Booked On",""].map(h=>(
                <TableCell key={h} sx={{ fontWeight:700,fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.4px",py:1.5,borderBottom:`2px solid ${T.border}` }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? Array.from({length:8}).map((_,i)=><SkelRow key={i} cols={10} />) :
              bookings.length===0 ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py:6 }}>
                  <ShieldRoundedIcon sx={{ fontSize:40,color:T.border,mb:1,display:"block",mx:"auto" }} />
                  <Typography fontSize={13} color="text.secondary">No protection bookings yet.</Typography>
                </TableCell></TableRow>
              ) : bookings.map(b => {
                const cust = b.Customer ? `${b.Customer.first_name||""} ${b.Customer.last_name||""}`.trim() : "—";
                const hasClaim = !!b.Claim;
                return (
                  <TableRow key={b.booking_id} hover sx={{ "&:last-child td":{ border:0 }, "&:hover":{ bgcolor:T.tealLt+"33" } }}>
                    <TableCell>
                      <Typography sx={{ fontSize:12,fontWeight:700,color:T.teal,fontFamily:"monospace" }}>{b.certificate_ref||"—"}</Typography>
                    </TableCell>
                    <TableCell>
                      {b.Package?.tracking_code ? (
                        <Box sx={{ display:"inline-flex",px:1.2,py:0.3,bgcolor:T.tealLt,borderRadius:"7px",border:`1px solid ${T.teal}22` }}>
                          <Typography sx={{ fontSize:11,fontWeight:700,color:T.teal,fontFamily:"monospace" }}>{b.Package.tracking_code}</Typography>
                        </Box>
                      ) : <Typography sx={{ fontSize:12,color:T.muted }}>—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize:12,fontWeight:600,color:T.text }}>{cust}</Typography>
                      {b.Customer?.mobile_num && <Typography sx={{ fontSize:10,color:T.muted }}>{b.Customer.mobile_num}</Typography>}
                    </TableCell>
                    <TableCell><PlanChip tier={b.plan_tier} /></TableCell>
                    <TableCell><Typography sx={{ fontSize:13,fontWeight:700,color:T.text }}>{fmtRs(b.declared_value)}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize:12,color:T.emerald,fontWeight:600 }}>{fmtRs(b.premium_amount)}</Typography></TableCell>
                    <TableCell>
                      {b.Trip?.id ? (
                        <Box sx={{ display:"inline-flex",px:1,py:0.3,bgcolor:T.bg,border:`1px solid ${T.border}`,borderRadius:"6px" }}>
                          <Typography sx={{ fontSize:11,fontWeight:700,color:T.muted,fontFamily:"monospace" }}>#{b.Trip.id}</Typography>
                        </Box>
                      ) : <Typography sx={{ fontSize:12,color:T.muted }}>—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display:"inline-flex",alignItems:"center",gap:0.5,px:1,py:0.3,borderRadius:"7px", bgcolor:hasClaim?T.roseLt:T.emeraldLt, border:`1px solid ${hasClaim?T.rose:T.emerald}22` }}>
                        <Box sx={{ width:5,height:5,borderRadius:"50%",bgcolor:hasClaim?T.rose:T.emerald }} />
                        <Typography sx={{ fontSize:10,fontWeight:700,color:hasClaim?T.rose:T.emerald }}>{hasClaim?"Claimed":"Active"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell><Typography sx={{ fontSize:12,color:T.muted }}>{fmtShort(b.createdAt)}</Typography></TableCell>
                  </TableRow>
                );
              })
            }
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && bookings.length > 0 && <Paginator page={page} totalPages={totalPages} perPage={perPage} setPerPage={setPerPage} setPage={setPage} total={total} />}
    </Card>
  );
}

// ── Ledger sub-tab ────────────────────────────────────────────────────────
function LedgerTab() {
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [page,    setPage]      = useState(1);
  const [perPage, setPerPage]   = useState(20);
  const [total,   setTotal]     = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getData(API_ENDPOINTS.getProtectionFundLedger, { page, limit: perPage });
      const r = res?.result || res?.data?.result;
      if (r) { setEntries(r.entries||[]); setTotal(r.pagination?.total||0); setTotalPages(r.pagination?.totalPages||1); }
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [page, perPage]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card sx={{ borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", overflow:"hidden" }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor:T.bg }}>
              {["Date","Type","Amount","Balance After","Reference","Description"].map(h=>(
                <TableCell key={h} sx={{ fontWeight:700,fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.4px",py:1.5,borderBottom:`2px solid ${T.border}` }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? Array.from({length:10}).map((_,i)=><SkelRow key={i} cols={6} />) :
              entries.length===0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py:6 }}>
                  <ReceiptLongRoundedIcon sx={{ fontSize:40,color:T.border,mb:1,display:"block",mx:"auto" }} />
                  <Typography fontSize={13} color="text.secondary">No ledger entries yet.</Typography>
                </TableCell></TableRow>
              ) : entries.map(e => {
                const isCredit = e.type==="credit";
                return (
                  <TableRow key={e.id} hover sx={{ "&:last-child td":{ border:0 } }}>
                    <TableCell><Typography sx={{ fontSize:12,color:T.muted }}>{fmtDate(e.createdAt)}</Typography></TableCell>
                    <TableCell>
                      <Box sx={{ display:"inline-flex",alignItems:"center",gap:0.5,px:1.2,py:0.3,borderRadius:"8px", bgcolor:isCredit?T.emeraldLt:T.roseLt, border:`1px solid ${isCredit?T.emerald:T.rose}22` }}>
                        <Box sx={{ width:5,height:5,borderRadius:"50%",bgcolor:isCredit?T.emerald:T.rose }} />
                        <Typography sx={{ fontSize:11,fontWeight:700,color:isCredit?T.emerald:T.rose,textTransform:"uppercase" }}>{e.type}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize:13,fontWeight:800,color:isCredit?T.emerald:T.rose, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                        {isCredit?"+":"-"}{fmtRs(e.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography sx={{ fontSize:12,fontWeight:600,color:T.text }}>{fmtRs(e.balance_after)}</Typography></TableCell>
                    <TableCell>
                      <Box sx={{ display:"inline-flex",px:1,py:0.3,bgcolor:T.bg,border:`1px solid ${T.border}`,borderRadius:"6px" }}>
                        <Typography sx={{ fontSize:11,fontWeight:600,color:T.muted,textTransform:"capitalize" }}>{e.reference_type||"—"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth:240 }}>
                      <Typography sx={{ fontSize:12,color:T.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:240 }}>{e.description||"—"}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            }
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && entries.length > 0 && <Paginator page={page} totalPages={totalPages} perPage={perPage} setPerPage={setPerPage} setPage={setPage} total={total} />}
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function ProtectionFundDashboard() {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [subTab,   setSubTab]   = useState("protections");

  useEffect(() => {
    (async () => {
      try {
        const res = await getData(API_ENDPOINTS.getProtectionFund);
        const r = res?.result || res?.data?.result;
        if (r) setData(r);
      } catch(e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const fundStatus = data?.fundHealth?.status;
  const fundColor  = fundStatus==="healthy"?T.emerald:fundStatus==="warning"?T.amber:T.rose;
  const fundBg     = fundStatus==="healthy"?T.emeraldLt:fundStatus==="warning"?T.amberLt:T.roseLt;

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

      {/* Hero header */}
      <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`, borderRadius:"20px", p:3, mb:3,
        display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
        boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>
          <Box sx={{ width:46, height:46, borderRadius:"12px", bgcolor:"rgba(255,255,255,0.18)", border:"1px solid rgba(255,255,255,0.25)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <AccountBalanceRoundedIcon sx={{ fontSize:24, color:"#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize:22, fontWeight:800, color:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Cargo Protection</Typography>
            <Typography sx={{ fontSize:13, color:"rgba(255,255,255,0.75)", mt:0.3 }}>Fund balance, protections, and ledger</Typography>
          </Box>
        </Box>
        {/* Fund health quick pill */}
        {!loading && data && (
          <Box sx={{ display:"flex", alignItems:"center", gap:1, bgcolor:fundBg, border:`1px solid ${fundColor}33`, borderRadius:"12px", px:2, py:1 }}>
            <Box sx={{ width:8, height:8, borderRadius:"50%", bgcolor:fundColor }} />
            <Typography sx={{ fontSize:13, fontWeight:700, color:fundColor }}>
              Fund: {fmtRs(data.balance)} — {fundStatus?.toUpperCase()}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Outer nav */}
      <ProtectionNav activeTab={2} />

      {/* KPI tiles */}
      <Box sx={{ display:"flex", gap:2, flexWrap:"wrap", mb:3 }}>
        <KpiTile label="Fund Balance"        value={loading?"—":fmtRs(data?.balance)}        icon={<AccountBalanceRoundedIcon />} accent={fundColor}  iconBg={fundBg} loading={loading} sub={fundStatus?.toUpperCase()} />
        <KpiTile label="Premiums Collected"  value={loading?"—":fmtRs(data?.totalPremiums)}   icon={<TrendingUpRoundedIcon />}     accent={T.emerald} iconBg={T.emeraldLt} loading={loading} />
        <KpiTile label="Total Payouts"       value={loading?"—":fmtRs(data?.totalPayouts)}    icon={<TrendingDownRoundedIcon />}   accent={T.rose}    iconBg={T.roseLt}    loading={loading} />
        <KpiTile label="Net Balance"         value={loading?"—":fmtRs(data?.netBalance)}      icon={<CurrencyRupeeRoundedIcon />}  accent={data?.netBalance>=0?T.emerald:T.rose} iconBg={data?.netBalance>=0?T.emeraldLt:T.roseLt} loading={loading} />
      </Box>

      {/* Pause warning */}
      {data?.fundHealth?.premiumTierPaused && (
        <Alert severity="warning" sx={{ mb:3, borderRadius:"12px" }}>
          {data.fundHealth.allTiersPaused
            ? "ALL protection tiers are currently PAUSED due to low fund balance."
            : "Premium tier is currently PAUSED due to low fund balance."}
        </Alert>
      )}

      {/* Sub-tab toggle */}
      <Box sx={{ display:"flex", gap:0.5, bgcolor:"white", border:`1px solid ${T.border}`, borderRadius:"12px", p:0.5, mb:2.5, width:"fit-content" }}>
        <SubTab label="Protections" icon={<ShieldRoundedIcon />}       active={subTab==="protections"} onClick={()=>setSubTab("protections")} />
        <SubTab label="Ledger"      icon={<ReceiptLongRoundedIcon />}   active={subTab==="ledger"}      onClick={()=>setSubTab("ledger")} />
      </Box>

      {subTab==="protections" && <ProtectionsTab />}
      {subTab==="ledger"      && <LedgerTab />}
    </Box>
  );
}
